/**
 * The plate field that looks a vehicle up and fills the rest of the form.
 *
 * Extracted so CREATING a registration and CORRECTING one read from the same
 * place. The wizard, the entity-editor preview and the record's edit dialog
 * all render this, so a tenant whose plate field declares a lookup provider
 * gets the lookup wherever that field appears - rather than a plain text box
 * on the one surface nobody wired.
 *
 * Which sibling fields it fills is data: any field flagged `is_prepopulated`,
 * matched by `code_name` or by the column it declares through
 * `maps_to_column`. Nothing here names a tenant's field.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  resolvePrepopulatedValues,
  vehicleLookupValues,
} from '@/admin/components/shared/vehicle-lookup'
import { useVehicleLookup } from '@/admin/hooks/use-lookups'
import type { VehicleLookupResult } from '@/admin/hooks/use-lookups'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import type { FormContext } from '@/shared/lib/forms/generated-types'
import type { FormFieldMeta } from '@/shared/lib/forms/types'

export function VehicleLookupField({
  field,
  fields,
  formCtx,
}: {
  field: FormFieldMeta
  fields: FormFieldMeta[]
  formCtx: FormContext
}) {
  const { t } = useTranslation()
  const lookup = useVehicleLookup()
  // `null` means "untouched"; `''` is a real value the operator typed. A falsy
  // check conflates them and reinstates the stored plate whenever setFieldValue
  // has not landed yet. (Clearing any field in this dialog currently reverts
  // anyway - that is a separate, pre-existing fault in the shared edit form.)
  const [draftPlate, setDraftPlate] = useState<string | null>(null)
  const storedPlate = String(formCtx.values[field.code_name] ?? '')
  const plate = draftPlate ?? storedPlate

  function handlePlateChange(raw: string) {
    const cleaned = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 8)
    setDraftPlate(cleaned)
    void formCtx.setFieldValue(field.code_name, cleaned)
  }

  function applyLookupResult(data: VehicleLookupResult) {
    const valuesByKey = vehicleLookupValues(data)
    void formCtx.setFieldValue(field.code_name, plate)
    // Every prepopulated field describes the vehicle this plate identifies, so
    // a lookup REPLACES them all - it does not merge. Skipping the ones the
    // provider has no answer for left the previous vehicle's data in place:
    // a provider without a VIN produced a Kia wearing a Fiat's VIN, and
    // nothing on screen said so.
    for (const [code, value] of Object.entries(
      resolvePrepopulatedValues(fields, valuesByKey),
    )) {
      void formCtx.setFieldValue(code, value)
    }
  }

  function handleLookup() {
    if (!plate) return
    lookup.mutate(plate, {
      onSuccess: applyLookupResult,
    })
  }

  return (
    <div>
      <Label className="mb-1.5 block clara-label font-normal">
        {field.display_name}
        {field.required ? (
          <span
            aria-hidden="true"
            className="ml-[2px] mt-2 inline-block text-[17px] leading-[4px] text-[var(--status-error)]"
          >
            *
          </span>
        ) : null}
      </Label>
      <div className="flex gap-2">
        <Input
          type="text"
          value={plate}
          onChange={(event) => handlePlateChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleLookup()
            }
          }}
          placeholder={field.hint || 'e.g. AB21XYZ'}
          maxLength={8}
          autoComplete="off"
          data-lpignore="true"
          data-form-type="other"
          className="h-9 flex-1 uppercase tracking-[0.1em]"
        />
        <Button
          type="button"
          onClick={handleLookup}
          disabled={!plate || lookup.isPending}
          className="h-9 whitespace-nowrap"
        >
          {lookup.isPending
            ? t('vehicleLookup.lookingUp', 'Looking up...')
            : t('vehicleLookup.lookUp', 'Look Up')}
        </Button>
      </div>
      {lookup.isError ? (
        <p className="mt-2 text-[12px] text-[var(--ink-3)]">
          {t(
            'vehicleLookup.notFound',
            'Not found. Enter details manually below.',
          )}
        </p>
      ) : field.description ? (
        <p className="mt-1 text-[11px] text-[var(--ink-3)]">{field.description}</p>
      ) : null}
    </div>
  )
}

